import React, { useContext } from "react";
import { Select } from "@narmi/design_system";
import PropTypes from "prop-types";
import { useLocalization } from "@fluent/react";
import { FREQUENCIES } from "byzantine/src/Recurrence";

import InstitutionSettingsContext from "../contexts/InstitutionSettingsContext";

export const TRANSLATED_FREQUENCIES = Object.freeze({
  [FREQUENCIES.ONCE]: "frequency-once",
  [FREQUENCIES.DAILY]: "frequency-daily",
  [FREQUENCIES.WEEKLY]: "frequency-weekly",
  [FREQUENCIES.EVERY_TWO_WEEKS]: "frequency-every-two-weeks",
  [FREQUENCIES.FIRST_AND_FIFTEENTH]: "frequency-first-and-fifteenth",
  [FREQUENCIES.MONTHLY]: "frequency-monthly",
  [FREQUENCIES.YEARLY]: "frequency-yearly",
});

/**
 * Utility function to get the display string for a frequency value
 * @param {string} frequency - The frequency value (e.g., "ONCE", "WEEKLY", etc.)
 * @param {object} l10n - The localization object from useLocalization()
 * @returns {string|null} - The localized frequency string or null for "ONCE"
 */
export const getDisplayedFrequency = (frequency, l10n) => {
  if (!frequency || frequency === "ONCE") {
    return null;
  }
  return l10n.getString(
    TRANSLATED_FREQUENCIES[FREQUENCIES[frequency] || frequency]
  );
};

const FrequencySelector = ({
  label,
  onChange,
  value,
  excludedFrequencies = /** @type {any} */ ([]),
  error,
}) => {
  const { l10n } = useLocalization();
  const { disable_daily_transfer, disable_semimonthly_transfer } = useContext(
    InstitutionSettingsContext
  );
  return (
    <Select label={label} onChange={onChange} value={value} errorText={error}>
      {Object.values(FREQUENCIES)
        .filter(
          // Filter out frequencies if institutions have respective settings to disable them
          (f) =>
            !(f === FREQUENCIES.DAILY && disable_daily_transfer) &&
            !(
              f === FREQUENCIES.FIRST_AND_FIFTEENTH &&
              disable_semimonthly_transfer
            ) &&
            !(excludedFrequencies?.length && excludedFrequencies.includes(f))
        )
        .map((f) => (
          <Select.Item
            key={f}
            value={f}
            searchValue={TRANSLATED_FREQUENCIES[f]}
            onClick={() => onChange(f)}
          >
            {l10n.getString(TRANSLATED_FREQUENCIES[f])}
          </Select.Item>
        ))}
    </Select>
  );
};
FrequencySelector.propTypes = {
  label: PropTypes.string,
  onChange: PropTypes.func,
  value: PropTypes.string,
  excludedFrequencies: PropTypes.array,
  error: PropTypes.string,
};

export default FrequencySelector;
